// Detail / edit page for a single operation's pricing configuration.
// Route: /pricing/global/:action/:operationGroup/:operation/:id
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import {
  useGlobalPricingQuery,
  useExternalOperationDetailsQuery,
  useUpdateGlobalPricingMutation,
  useUpdateExternalOperationMutation,
  isInternalGroup,
  isExternalGroup,
} from './api';
import { PricingForm } from './components/PricingForm';
import {
  OperationActionEnum,
  OperationGroupEnum,
  isRevenueGroup,
} from '@/types/pricing';
import type { ExternalDistributionModel, PricingPayloadModel } from '@/types/pricing';

interface RouteParams {
  action: string;
  operationGroup: string;
  operation: string;
  id: string;
}

export function GlobalPricingUpdatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const rawParams = useParams() as unknown as RouteParams;
  const { action, operationGroup, operation, id } = rawParams;

  // Validate action param; redirect if invalid
  const validAction = Object.values(OperationActionEnum).includes(
    action as OperationActionEnum,
  );
  const parsedGroup = operationGroup as OperationGroupEnum;
  const operationId = Number(id);
  const isInternal = isInternalGroup(parsedGroup);
  const isExternal = isExternalGroup(parsedGroup);
  const isRevenue = isRevenueGroup(parsedGroup) || isExternal;

  // Navigate away for invalid params
  useEffect(() => {
    if (!validAction || (!isInternal && !isExternal) || isNaN(operationId)) {
      navigate('/pricing/global', { replace: true });
    }
  }, [validAction, isInternal, isExternal, operationId, navigate]);

  // Store fetched external distribution so we can merge changes into it
  const [externalDistribution, setExternalDistribution] =
    useState<ExternalDistributionModel | null>(null);

  const internalQuery = useGlobalPricingQuery(
    parsedGroup,
    operationId,
    validAction && isInternal,
  );

  const externalQuery = useExternalOperationDetailsQuery(
    operationId,
    validAction && isExternal,
  );

  // Capture external distribution details once loaded
  useEffect(() => {
    if (externalQuery.data) {
      setExternalDistribution(externalQuery.data);
    }
  }, [externalQuery.data]);

  const updateGlobal = useUpdateGlobalPricingMutation();
  const updateExternal = useUpdateExternalOperationMutation();

  const isSaving = updateGlobal.isPending || updateExternal.isPending;

  // Map external distribution to PricingPayloadModel for the form
  const externalFormData: PricingPayloadModel | null = externalQuery.data
    ? {
        id: externalQuery.data.nostroOperationId,
        distributionType: externalQuery.data.compensationType,
        operationType: externalQuery.data.microOperationId,
        grids: externalQuery.data.grids ?? [],
        percentage: externalQuery.data.margin ?? 0,
      }
    : null;

  const formData = isInternal ? (internalQuery.data ?? null) : externalFormData;
  const isLoading = isInternal ? internalQuery.isLoading : externalQuery.isLoading;

  const handleSave = async (payload: PricingPayloadModel) => {
    try {
      if (isExternal && externalDistribution) {
        const patchedExternal: ExternalDistributionModel = {
          ...externalDistribution,
          compensationType: payload.distributionType,
          margin: payload.percentage ?? 0,
          grids: payload.grids ?? [],
        };
        await updateExternal.mutateAsync(patchedExternal);
      } else if (isInternal) {
        await updateGlobal.mutateAsync({ payload, operationGroup: parsedGroup });
      }
      navigate('/pricing/global');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : t('messages.error', { defaultValue: 'Error' });
      await Swal.fire(
        t('messages.error', { defaultValue: 'Error' }),
        message,
        'error',
      );
    }
  };

  const handleChangeAction = (nextAction: OperationActionEnum) => {
    navigate(
      `/pricing/global/${nextAction}/${operationGroup}/${operation}/${id}`,
      { replace: true },
    );
  };

  return (
    <Box>
      <Paper sx={{ p: 3, minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1 }}
        >
          <Typography variant="h6">
            {t('pages.pricing.global_pricing.title', {
              defaultValue: 'Global Pricing',
            })}
            {' — '}
            {t(`pages.pricing.pricing_type.${operationGroup}`, {
              defaultValue: operationGroup,
            })}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/pricing/global')}
          >
            {t('global.buttons.back_to_global_pricing', {
              defaultValue: 'Back to Global Pricing',
            })}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
          {t(
            `pages.pricing.global_pricing.${action}_pricing_details`,
            { defaultValue: `${action} pricing details` },
          )}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t(`pages.pricing.operation_type.${operation}`, {
            defaultValue: operation,
          })}
        </Typography>

        {/* Form area */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <PricingForm
            mode="global"
            formData={formData}
            action={action as OperationActionEnum}
            operationType={operation}
            operationGroup={parsedGroup}
            isRevenueData={isRevenue}
            onSubmit={(data) => void handleSave(data)}
            onChangeAction={handleChangeAction}
            isSaving={isSaving}
          />
        )}
      </Paper>
    </Box>
  );
}
